import React from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, Users, Target, TrendingUp, CheckCircle } from 'lucide-react';

const FourQMethod = () => {
  const quadrants = [
    {
      id: 1,
      title: 'Engage',
      question: 'Why?',
      role: 'Facilitator',
      description: 'Spark curiosity and connect learning to real-world applications',
      icon: Lightbulb,
      color: 'from-yellow-400 to-orange-500'
    },
    {
      id: 2,
      title: 'Share',
      question: 'What?',
      role: 'Presenter',
      description: 'Present concepts clearly with visual aids and examples',
      icon: Users,
      color: 'from-blue-400 to-cyan-500'
    },
    {
      id: 3,
      title: 'Practice',
      question: 'How?',
      role: 'Coach',
      description: 'Guide hands-on practice with immediate feedback',
      icon: Target,
      color: 'from-green-400 to-emerald-500'
    },
    {
      id: 4,
      title: 'Perform',
      question: 'If?',
      role: 'Evaluator',
      description: 'Assess understanding through real-world problem solving',
      icon: TrendingUp,
      color: 'from-purple-400 to-pink-500'
    }
  ];

  const benefits = [
    'Complete understanding through 4-phase learning cycle',
    'Adaptive teaching roles based on student needs',
    'Critical thinking development with targeted questioning',
    'Proven 99% student satisfaction rate',
    'Better retention and application of concepts'
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-white via-cyan-50 to-blue-50 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden opacity-30">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-200 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-200 rounded-full filter blur-3xl" />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-block mb-4"
          >
            <span className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full text-sm font-bold shadow-lg">
              Our Unique Teaching Methodology
            </span>
          </motion.div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            The Revolutionary <span className="text-cyan-600">FourQ Method</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A scientifically-proven 4-quadrant approach that transforms how students learn, 
            ensuring deep understanding and lasting retention
          </p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
          {/* Left: Method Diagram */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative bg-white rounded-3xl p-8 shadow-2xl">
              <img 
                src="/fourq-method.jpg" 
                alt="FourQ Teaching Method Quadrants" 
                className="w-full h-auto rounded-2xl"
              />
              <div className="absolute -top-4 -right-4 bg-gradient-to-br from-green-400 to-emerald-500 text-white px-6 py-3 rounded-full shadow-xl">
                <span className="font-bold">99% Success Rate</span>
              </div>
            </div>

            {/* Floating stats */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl z-10"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="font-bold text-gray-900">4 Learning Phases</div>
                  <div className="text-sm text-gray-600">Complete Mastery</div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right: Quadrant Explanations */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                How the FourQ Method Works
              </h3>
              <p className="text-gray-600 mb-6">
                Our unique methodology adapts to each student's learning journey, 
                guiding them through four distinct phases that build upon each other.
              </p>
              
              <div className="space-y-4">
                {quadrants.map((quadrant, index) => {
                  const Icon = quadrant.icon;
                  return (
                    <motion.div
                      key={quadrant.id}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1, duration: 0.4 }}
                      className="flex items-start gap-4 p-4 rounded-xl bg-gradient-to-r from-cyan-50 to-blue-50 hover:shadow-md transition-all duration-300"
                    >
                      <div className={`w-12 h-12 bg-gradient-to-br ${quadrant.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-bold text-gray-900">{quadrant.title}</h4>
                          <span className="text-xs px-2 py-1 bg-white rounded-full text-gray-600">
                            {quadrant.role}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{quadrant.description}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-3xl p-8 lg:p-12 shadow-2xl"
        >
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold text-white mb-4">
                Why FourQ Method is Game-Changing
              </h3>
              <p className="text-cyan-100 text-lg">
                Unlike traditional one-size-fits-all teaching, our method adapts to each student's 
                unique learning style, ensuring no child is left behind.
              </p>
            </div>
            <div className="space-y-3">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                  className="flex items-center gap-3 bg-white/10 backdrop-blur-sm p-4 rounded-xl"
                >
                  <CheckCircle className="w-6 h-6 text-green-300 flex-shrink-0" />
                  <span className="text-white font-medium">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FourQMethod;
